// components/Clinician/SchedulingBoard.jsx
// Day-view scheduling board: one column per active Room/Equipment, showing that
// room's booked orders for the selected day. Reuses the existing CreateOrder
// dialog (in both create and edit mode) rather than duplicating order logic.
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, TextField, MenuItem, Paper, Chip, IconButton,
  CircularProgress, Button, Tooltip
} from '@mui/material';
import { Add as AddIcon, Refresh as RefreshIcon } from '@mui/icons-material';
import { useThemeMode } from '../../contexts/ThemeModeContext';
import { orderService, roomService } from '../../services/ris';
import CreateOrder from './CreateOrder';

const MODALITIES = ['CT', 'MRI', 'XRAY', 'MAMMO', 'US', 'ANGIO', 'BMD', 'FLUORO', 'CR', 'DR', 'MR', 'MG', 'PT', 'NM', 'OT', 'DX'];

const STATUS_COLORS = {
  NEW: { bg: '#EFF6FF', text: '#2563EB' },
  ARRIVAL: { bg: '#EFF6FF', text: '#2563EB' },
  READY: { bg: '#FEF3C7', text: '#D97706' },
  STARTED: { bg: '#FEF3C7', text: '#D97706' },
  ENDEXAM: { bg: '#FEF3C7', text: '#D97706' },
  QC: { bg: '#FEF3C7', text: '#D97706' },
  TOREPORT: { bg: '#FEF3C7', text: '#D97706' },
  PRELIM: { bg: '#FEF3C7', text: '#D97706' },
  REPORTED: { bg: '#D1FAE5', text: '#059669' },
  CANCELLED: { bg: '#FEE2E2', text: '#DC2626' }
};

export default function SchedulingBoard() {
  const { mode } = useThemeMode();
  const isDark = mode === 'dark';
  const textColorPrimary = isDark ? '#F1F5F9' : '#0F172A';
  const textColorSecondary = isDark ? '#CBD5E1' : '#475569';
  const cardBg = isDark ? '#1E1E1E' : '#FFFFFF';
  const cardBorder = isDark ? '1px solid rgba(255,255,255,0.1)' : '1px solid #E2E8F0';
  const cardBorderColor = isDark ? 'rgba(255,255,255,0.1)' : '#E2E8F0';
  const columnBg = isDark ? '#141416' : '#F8FAFC';
  const rowHoverBg = isDark ? 'rgba(255,255,255,0.04)' : '#F8FAFC';

  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [modalityFilter, setModalityFilter] = useState('');
  const [rooms, setRooms] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);

  const [createOpen, setCreateOpen] = useState(false);
  const [createSeed, setCreateSeed] = useState(null);
  const [editOrder, setEditOrder] = useState(null);

  const fetchBoard = useCallback(async () => {
    setLoading(true);
    try {
      const [roomsRes, scheduleRes] = await Promise.all([
        roomService.list(modalityFilter ? { modality: modalityFilter } : {}),
        orderService.getSchedule({
          scheduledDateFrom: date,
          scheduledDateTo: date,
          ...(modalityFilter ? { modality: modalityFilter } : {})
        })
      ]);
      setRooms(roomsRes.data.rooms || []);
      setOrders(scheduleRes.data.orders || []);
    } catch (err) {
      console.error('Failed to load scheduling board:', err);
    } finally {
      setLoading(false);
    }
  }, [date, modalityFilter]);

  useEffect(() => { fetchBoard(); }, [fetchBoard]);

  const ordersForRoom = (roomId) =>
    orders
      .filter(o => (o.assignedRoom?._id || o.assignedRoom) === roomId)
      .sort((a, b) => (a.scheduledTime || '').localeCompare(b.scheduledTime || ''));

  const unassignedOrders = orders
    .filter(o => !o.assignedRoom)
    .sort((a, b) => (a.scheduledTime || '').localeCompare(b.scheduledTime || ''));

  const openCreateInRoom = (room) => {
    setCreateSeed({ scheduledDate: date, modality: room.modality, roomId: room._id });
    setEditOrder(null);
    setCreateOpen(true);
  };

  const openEditOrder = async (order) => {
    try {
      const { data } = await orderService.getById(order._id);
      setEditOrder(data.order || data);
      setCreateSeed(null);
      setCreateOpen(true);
    } catch (err) {
      console.error('Failed to load order for editing:', err);
    }
  };

  const renderOrderCard = (order) => {
    const colors = STATUS_COLORS[order.orderStatus] || STATUS_COLORS.NEW;
    return (
      <Paper
        key={order._id}
        variant="outlined"
        onClick={() => openEditOrder(order)}
        sx={{
          p: 1, mb: 0.75, cursor: 'pointer', borderRadius: '6px',
          borderColor: cardBorderColor, bgcolor: cardBg,
          '&:hover': { borderColor: '#0D9488', bgcolor: rowHoverBg }
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
          <Typography variant="body2" fontWeight={700} sx={{ fontSize: '0.78rem', color: textColorPrimary }}>
            {order.scheduledTime || '--:--'}
          </Typography>
          {order.priority === 'STAT' && <Chip label="STAT" size="small" color="error" sx={{ height: 16, fontSize: 9 }} />}
        </Box>
        <Typography variant="body2" fontWeight={600} sx={{ mt: 0.25, fontSize: '0.75rem', color: textColorPrimary }}>
          {order.patientName?.replace(/\^/g, ' ') || 'Unknown Patient'}
        </Typography>
        <Typography variant="caption" sx={{ color: textColorSecondary, display: 'block', fontSize: '0.68rem' }}>
          MRN: {order.patientMrn || '-'}
        </Typography>
        <Typography variant="caption" sx={{ color: textColorSecondary, display: 'block', fontSize: '0.68rem' }} noWrap>
          {order.procedure?.description || order.procedureDescription || ''}
        </Typography>
        <Chip
          label={order.orderStatus}
          size="small"
          sx={{ mt: 0.5, bgcolor: isDark ? 'rgba(255,255,255,0.08)' : colors.bg, color: colors.text, fontWeight: 600, height: 18, fontSize: 10 }}
        />
      </Paper>
    );
  };

  const sortedRooms = [...rooms].sort((a, b) => a.modality.localeCompare(b.modality) || a.name.localeCompare(b.name));

  return (
    <Box sx={{ p: { xs: 1, sm: 1.5 }, pb: 2 }}>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center', mb: 1.5 }}>
        <Typography variant="h6" fontWeight={700} sx={{ mr: 'auto', fontSize: '1.15rem', color: textColorPrimary }}>
          Scheduling Board
        </Typography>
        <TextField
          type="date" size="small" value={date}
          onChange={(e) => setDate(e.target.value)}
          sx={{
            '& .MuiOutlinedInput-root': {
              height: '30px',
              fontSize: '0.78rem',
              borderRadius: '6px',
              bgcolor: cardBg,
              color: textColorPrimary,
              '& fieldset': { borderColor: cardBorderColor }
            }
          }}
        />
        <TextField
          select size="small" value={modalityFilter}
          onChange={(e) => setModalityFilter(e.target.value)}
          sx={{
            minWidth: 130,
            '& .MuiOutlinedInput-root': {
              height: '30px',
              fontSize: '0.78rem',
              borderRadius: '6px',
              bgcolor: cardBg,
              color: textColorPrimary,
              '& fieldset': { borderColor: cardBorderColor }
            }
          }}
        >
          <MenuItem value="" sx={{ fontSize: '0.78rem' }}>All Modalities</MenuItem>
          {MODALITIES.map(m => <MenuItem key={m} value={m} sx={{ fontSize: '0.78rem' }}>{m}</MenuItem>)}
        </TextField>
        <Tooltip title="Refresh">
          <IconButton onClick={fetchBoard} size="small" sx={{ width: 30, height: 30, border: cardBorder, borderRadius: '6px', color: textColorSecondary }}>
            <RefreshIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress size={28} sx={{ color: '#4F46E5' }} /></Box>
      ) : sortedRooms.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center', border: cardBorder, bgcolor: cardBg }}>
          <Typography sx={{ color: textColorSecondary, fontSize: '0.875rem' }}>
            No rooms configured{modalityFilter ? ` for ${modalityFilter}` : ''}. Add rooms under Admin &rsaquo; Rooms &amp; Equipment.
          </Typography>
        </Paper>
      ) : (
        <Box sx={{ display: 'flex', gap: 1.5, overflowX: 'auto', pb: 1 }}>
          {unassignedOrders.length > 0 && (
            <Box sx={{ minWidth: 240, flexShrink: 0 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="subtitle2" fontWeight={700} sx={{ color: textColorPrimary, fontSize: '0.8rem' }}>Unassigned</Typography>
                <Chip label={unassignedOrders.length} size="small" sx={{ height: 18, fontSize: 10 }} />
              </Box>
              {unassignedOrders.map(renderOrderCard)}
            </Box>
          )}
          {sortedRooms.map((room) => {
            const roomOrders = ordersForRoom(room._id);
            return (
              <Box key={room._id} sx={{ minWidth: 240, flexShrink: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                  <Box>
                    <Typography variant="subtitle2" fontWeight={700} sx={{ color: textColorPrimary, fontSize: '0.8rem' }}>{room.name}</Typography>
                    <Chip label={room.modality} size="small" sx={{ height: 18, fontSize: 10 }} />
                  </Box>
                  <Tooltip title={`Schedule a new order in ${room.name}`}>
                    <IconButton size="small" onClick={() => openCreateInRoom(room)} sx={{ color: textColorSecondary }}>
                      <AddIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
                <Box sx={{ minHeight: 80, bgcolor: columnBg, border: cardBorder, borderRadius: '8px', p: roomOrders.length ? 0.5 : 1.5 }}>
                  {roomOrders.length === 0 ? (
                    <Button fullWidth size="small" onClick={() => openCreateInRoom(room)} sx={{ color: textColorSecondary, fontSize: '0.78rem' }}>
                      No orders — click to add
                    </Button>
                  ) : roomOrders.map(renderOrderCard)}
                </Box>
              </Box>
            );
          })}
        </Box>
      )}

      <CreateOrder
        open={createOpen}
        editOrder={editOrder}
        initialScheduling={createSeed}
        onClose={() => { setCreateOpen(false); setEditOrder(null); setCreateSeed(null); }}
        onSuccess={() => { setCreateOpen(false); setEditOrder(null); setCreateSeed(null); fetchBoard(); }}
      />
    </Box>
  );
}
